import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaMinus } from "react-icons/fa";
import '../Finance/Finance.css'
import projectHero from "../../../assets/Services/Project.png"; //
import CustomButton from "../../Button/CustomButton";

const ProjectDevelopment = () => {
  const [activeFAQ, setActiveFAQ] = useState(null);
  const navigate = useNavigate();

  const faqData = [
    {
      q: "What does Dazbog do in project development?",
      a: "We support projects from early planning by reviewing design, site conditions, energy yield, and overall technical feasibility.",
    },
    {
      q: "What is included in technical due diligence?",
      a: " It includes design review, energy yield assessment, site and resource analysis, risk identification, and bankability checks.",
    },
    {
      q: "Which types of projects do you support?",
      a: "We work on solar, wind, BESS, and hybrid renewable energy projects across utility-scale and distributed projects.",
    },
  ];

  // Animation variants for sections
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };


  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  return (
    <div className="service-detail-page">
      {/* --- HERO SECTION --- */}
      <section className="detail-hero">
        <div className="container">
          <div className="detail-hero-flex">
            <motion.div
              className="detail-hero-content"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>
                Project Development & Technical Due Diligence for Renewable
                Energy Projects
              </h1>
              <p>We help renewable energy projects move forward with clarity and confidence.
              From early planning to execution readiness, we focus on making projects technically strong, practical, and ready for investors.</p>
              <CustomButton
                variant="white"
                size="lg"
                text=" Discuss Your Project"
                onClick={() => navigate("/contact")}
              />
            </motion.div>
            <motion.div
              className="detail-hero-image"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
            >
              <img src={projectHero} alt="Project Development" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- HOW WE SUPPORT SECTION --- */}
      <section className="support-info-section">
        <div className="container">
          <motion.div
            className="support-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <h2>How We Support Your Project</h2>
            <p >Every renewable energy project starts with questions.</p>
            <p >Is the design strong?<br />Are there hidden risks?<br />Will lenders be comfortable with it?</p>
            <p>Our role is to help answer these questions early.</p>
            <p className="support-lead">We work closely with developers and EPC teams to review technical details, identify possible risks, and make sure the project is realistic and workable. The goal is simple — reduce uncertainty and build confidence before moving ahead.</p>
          </motion.div>

          <div className="services-list-container">
            <motion.h3
              className="list-main-title"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              Our Project Development & Technical Services
            </motion.h3>

            <motion.div
              className="services-breakdown-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {/* Service Blocks with stagger reveal */}
              {[
                {
                  title: "1. Technical Due Diligence",
                  text1: "Before construction or funding begins, it’s important to check everything properly.",
                  text2: "We review the technical side of the project to make sure the design, equipment, and planning are aligned.",
                  listTitle: "Our support includes:",
                  list: [
                    "Reviewing project layouts and designs",
                    "Checking technology and equipment choices",
                    "Assessing construction readiness",
                    "Identifying technical gaps early",
                  ],
                  outro: "This helps avoid costly changes later."
                },
                {
                  title: "2. Energy Yield & Resource Assessment",
                  text1: "Performance matters in renewable energy projects.",
                  text2: "We evaluate how much energy the project is expected to generate and whether those estimates are realistic.",
                  listTitle: "This includes:",
                  list: [
                    "Reviewing solar, wind, or hybrid resource data",
                    "Checking energy yield assumptions",
                    "Evaluating system efficiency and possible losses",
                  ],
                  outro: "Clear performance understanding builds investor confidence."
                },
                {
                  title: "3. Owner’s Engineer & Engineering Support",
                  text1: "Sometimes project owners need a technical partner who represents their interests.",
                  text2: "We act as the owner’s technical advisor and stay involved throughout the project lifecycle.",
                  listTitle: "Our role may include:",
                  list: [
                    "Reviewing engineering designs",
                    "Supporting coordination with EPC teams",
                    "Reviewing technical changes",
                    "Providing independent technical guidance",
                  ],
                  outro: "We help ensure decisions are practical and well-informed."
                },
                {
                  title: "4. Feasibility & Project Planning",
                  text1: "Not every idea is ready to move forward immediately.",
                  text2: "We help assess whether a project is technically and practically viable.",
                  listTitle: "Our support includes:",
                  list: [
                    "Technical feasibility studies",
                    "Site and infrastructure assessment",
                    "Reviewing project timelines and schedules",
                    "Identifying potential challenges in advance",
                  ],
                  outro: "Careful planning at this stage saves time and reduces risk later."
                },
              ].map((service, index) => (
                <motion.div
                  key={index}
                  className="service-text-block"
                  variants={fadeInUp}
                >
                  <h4>{service.title}</h4>
                  <p>{service.text1}</p>
                  <p>{service.text2}</p>
                  <p>{service.listTitle}</p>
                  <ul>
                    {service.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>{service.outro}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="projects-tags-box"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >

            <h3>Projects We Work On</h3>
            <div className="tag-flex">
              {[
                { name: "Solar IPP", path: "/projects/solar-ipp" },
                { name: "Solar EPC Project", path: "/projects/solar-epc" },
                { name: "Solar Hybrid EPC Project", path: "/projects/solar-hybrid" },
                { name: "Solar + BESS EPC Project", path: "/projects/solar-bess" }
              ].map((tag, i) => (
                <span
                  key={i}
                  className="project-tag-link"
                  onClick={() => navigate(tag.path)}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="faq-detail-section">
        <div className="container">
          <div className="faq-flex-row">
            <motion.div
              className="faq-left-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2>Project Development & Technical Due Diligence FAQs</h2>
              <p>
                Answers to common questions about our role during project
                execution.
              </p>
            </motion.div>
            <div className="faq-right-accordion">
              {faqData.map((faq, i) => (
                <motion.div
                  key={i}
                  className="faq-detail-item"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <div
                    className="faq-detail-q"
                    onClick={() => setActiveFAQ(activeFAQ === i ? null : i)}
                  >
                    <span>{faq.q}</span>
                    {activeFAQ === i ? (
                      <FaMinus className="faq-icon" />
                    ) : (
                      <FaPlus className="faq-icon" />
                    )}
                  </div>
                  <AnimatePresence>
                    {activeFAQ === i && (
                      <motion.div
                        className="faq-detail-a"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDevelopment;
